import React from 'react';
import { Provider } from 'react-redux';
import configureStore from './configureStore';
import './App.css';
import { Route, Switch } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import Layout from './Layout';
import configureApollo from './configureApollo';
import { AsyncTask, SessionProvider, NotFound } from './components';
import { useSession } from './components/SessionProvider';
import { ScrollToTopOnAnyNavigation } from './components/ScrollToTopOnNavigation';
import 'font-awesome/scss/font-awesome.scss';
import 'jbc-front/styles/default.scss';
import { useNotify } from './hooks/useNotify';
import { hasPermission } from './utils/permission';
import { NotificationsProvider } from 'reapop';
import './libs/telemetry/init';

// pages
import * as pages from './pages';
import { Top } from './pages/index/Top';
import { SearchPersonalResults } from './pages/regular_health_checkup/personal_results/index';
import { ImportPersonalResults } from './pages/regular_health_checkup/personal_results/import';
import { ShowPersonalResults } from './pages/regular_health_checkup/personal_results/show';
import { DeletePersonalResults } from './pages/regular_health_checkup/personal_results/delete';
import { CreatePersonalResult } from './pages/regular_health_checkup/personal_results/create';
import { EditPersonalResult } from './pages/regular_health_checkup/personal_results/edit';
import { SearchReports } from './pages/regular_health_checkup/reports/index';
import { CreateReport } from './pages/regular_health_checkup/reports/create';
import { EditReport } from './pages/regular_health_checkup/reports/edit';
import { CopyReport } from './pages/regular_health_checkup/reports/copy';
import { ShowReports } from './pages/regular_health_checkup/reports/show/ShowReports';
import { HealthCheckupInstitution } from './pages/settings/health_checkup_institutions/index/index';
import { CreateHealthCheckupInstitution } from './pages/settings/health_checkup_institutions/create';
import { EditHealthCheckupInstitution } from './pages/settings/health_checkup_institutions/edit';
import { ImportHealthCheckupInstitution } from './pages/settings/import_health_checkup_institution/index/index';
import { Users } from './pages/settings/users/index';
import { CreateUser } from './pages/settings/users/create';
import { InviteUser } from './pages/settings/users/invite';
import { EditUser } from './pages/settings/users/edit';

const store = configureStore({});
const client = configureApollo('/graphql', null);

const RedirectToDashboard = ({ history }) => {
  const notify = useNotify();
  history.push('/');
  notify('ログイン済みです', 'success');
  return <div></div>;
};

const RegularHealthCheckupRoutes = () => {
  const { user } = useSession();

  return (
    <Switch>
      {hasPermission(user, 'regular_health_checkup_access') && (
        <Switch>
          <Route path="/regular_health_checkup/personal_results" exact>
            <SearchPersonalResults />
          </Route>
          <Route path="/regular_health_checkup/personal_results/import" exact>
            <ImportPersonalResults />
          </Route>
          <Route path="/regular_health_checkup/personal_results/:personalId/create" exact>
            <CreatePersonalResult />
          </Route>
          <Route path="/regular_health_checkup/personal_results/:personalId/delete" exact>
            <DeletePersonalResults />
          </Route>
          <Route path="/regular_health_checkup/personal_results/:personalId/:resultId?" exact>
            <ShowPersonalResults />
          </Route>
          <Route path="/regular_health_checkup/personal_results/:personalId/:resultId/edit" exact>
            <EditPersonalResult />
          </Route>
          <Route path="/regular_health_checkup/reports" exact>
            <SearchReports />
          </Route>
          <Route path="/regular_health_checkup/reports/create" exact>
            <CreateReport />
          </Route>
          <Route path="/regular_health_checkup/reports/:id/edit" exact>
            <EditReport />
          </Route>
          <Route path="/regular_health_checkup/reports/:id/copy" exact>
            <CopyReport />
          </Route>
          <Route path="/regular_health_checkup/reports/:id" exact>
            <ShowReports />
          </Route>
        </Switch>
      )}
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
};

const SettingsRoutes = () => {
  const { user } = useSession();

  const routes: { path: string; exact?: boolean; Component: React.ComponentType }[] = [
    { path: '/settings', exact: true, Component: pages.Settings }
  ];

  // ストレスチェック
  if (hasPermission(user, 'stress_check_access')) {
    routes.push({ path: '/settings/survey_forms', exact: true, Component: pages.SurveyForms });
    routes.push({ path: '/settings/survey_forms/:id', exact: true, Component: pages.SurveyFormDetail });
    routes.push({ path: '/settings/mails', exact: true, Component: pages.Mails });
    routes.push({ path: '/settings/mails/create', exact: true, Component: pages.CreateMail });
    routes.push({ path: '/settings/mails/edit/:id', exact: true, Component: pages.EditMail });
    routes.push({ path: '/settings/implementation_surveys', exact: true, Component: pages.ImplementationSurveys });
  }

  // 定期健康診断
  if (hasPermission(user, 'regular_health_checkup_access')) {
    routes.push({ path: '/settings/health_checkup_institution', exact: true, Component: HealthCheckupInstitution });
    routes.push({
      path: '/settings/health_checkup_institution/create',
      exact: true,
      Component: CreateHealthCheckupInstitution
    });
    routes.push({
      path: '/settings/health_checkup_institution/edit/:id',
      exact: true,
      Component: EditHealthCheckupInstitution
    });
    routes.push({
      path: '/settings/import_health_checkup_institution',
      exact: true,
      Component: ImportHealthCheckupInstitution
    });
  }

  // 権限
  if (hasPermission(user, 'user_manage_list_users')) {
    routes.push({ path: '/settings/users', exact: true, Component: Users });
  }
  if (hasPermission(user, 'user_manage_add_user')) {
    routes.push({ path: '/settings/users/create', exact: true, Component: CreateUser });
  }
  if (hasPermission(user, 'user_manage_add_lms_user')) {
    routes.push({ path: '/settings/users/invite', exact: true, Component: InviteUser });
  }
  if (
    hasPermission(user, 'user_manage_edit_stress_check_roles') ||
    hasPermission(user, 'user_manage_edit_regular_health_checkup_roles') ||
    hasPermission(user, 'user_manage_edit_user_manage_roles')
  ) {
    routes.push({ path: '/settings/users/:id/edit', exact: true, Component: EditUser });
  }

  return (
    <Switch>
      {routes.map(({ path, exact, Component }) => (
        <Route key={path} path={path} exact={exact} component={Component} />
      ))}
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
};

const App = () => (
  <ApolloProvider client={client}>
    <Provider store={store}>
      <NotificationsProvider>
        <BrowserRouter>
          <ScrollToTopOnAnyNavigation />
          <SessionProvider>
            <AsyncTask>
              <Layout sidebar showSession>
                <Switch>
                  <Route path="/" exact>
                    <Top />
                  </Route>
                  <Route path="/dashboard" exact component={pages.Dashboard} />
                  <Route path="/dashboard/:displaySurvey" exact component={pages.Dashboard} />
                  <Route path="/dashboard/:displaySurvey/:surveyId" exact component={pages.Dashboard} />
                  <Route path="/employees" exact component={pages.Employees} />
                  <Route path="/personal_results" exact component={pages.PersonalResults} />
                  <Route path="/personal_results/:surveyId" exact component={pages.PersonalResults} />
                  <Route path="/personal_results/details/:id" exact component={pages.PersonalResultDetail} />
                  <Route path="/group_result" exact component={pages.GroupResult} />
                  <Route path="/group_result/:surveyId" exact component={pages.GroupResult} />
                  <Route path="/regular_health_checkup">
                    <RegularHealthCheckupRoutes />
                  </Route>
                  <Route path="/settings">
                    <SettingsRoutes />
                  </Route>
                  <Route path="/invitation_confirmation" exact component={RedirectToDashboard} />
                  <Route path="/login" exact component={RedirectToDashboard} />
                  <Route path="/reset_password" exact component={RedirectToDashboard} />
                  <Route path="/logout" exact component={pages.Logout} />
                  <Route component={NotFound} />
                </Switch>
              </Layout>
            </AsyncTask>
          </SessionProvider>
        </BrowserRouter>
      </NotificationsProvider>
    </Provider>
  </ApolloProvider>
);

export default App;
